import React from 'react';
import { CreditCard, Smartphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getBootLocale } from '../i18n';

/**
 * PaymentMethodDialog — small chooser between credit card (Tranzila iframe)
 * and BIT/GamaPay. Shown only when both options are available; the parent
 * routes the choice to the matching purchase helper.
 *
 * Bilingual: all donor-facing labels go through useTranslation(). The dialog
 * direction follows the boot locale (he → RTL, en → LTR) so the layout reads
 * naturally for the donor's language.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (method: 'credit' | 'bit') => void;
  title?: string;
  description?: string;
}

const PaymentMethodDialog: React.FC<Props> = ({
  open,
  onClose,
  onSelect,
  title,
  description,
}) => {
  const { t } = useTranslation();
  if (!open) return null;
  const dir = getBootLocale() === 'en' ? 'ltr' : 'rtl';
  const textAlign = dir === 'ltr' ? 'text-left' : 'text-right';
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" dir={dir}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative">
        <button
          onClick={onClose}
          className={`absolute top-3 ${dir === 'rtl' ? 'left-3' : 'right-3'} text-gray-400 hover:text-gray-700`}
          aria-label={t('common.close')}
        >
          <X size={20} />
        </button>
        <h2 className="text-lg font-bold text-gray-900 mb-1">
          {title || t('public.payment.method.dialogTitle')}
        </h2>
        {description && <p className="text-xs text-gray-500 mb-4">{description}</p>}

        <div className="space-y-3 mt-4">
          <button
            onClick={() => onSelect('credit')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 transition-colors ${textAlign}`}
          >
            <CreditCard size={22} className="text-blue-600 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-gray-900">{t('public.payment.method.creditCard')}</p>
              <p className="text-xs text-gray-500">{t('public.payment.method.creditCardDesc')}</p>
            </div>
          </button>

          <button
            onClick={() => onSelect('bit')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-pink-200 hover:border-pink-500 hover:bg-pink-50 transition-colors ${textAlign}`}
          >
            <Smartphone size={22} className="text-pink-600 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-gray-900">{t('public.payment.method.bit')}</p>
              <p className="text-xs text-gray-500">{t('public.payment.method.bitDesc')}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodDialog;
